import { Card, CardContent, Checkbox, Divider, FormControl, FormLabel, Stack } from '@mui/joy';
import { ChangeEvent } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { QuotePartNoteType } from 'quotes/enums';
import { Color, Size, Variant } from 'theme/enums';

import { IQuotePartRemoveNotesForm } from './types';
import { getNoteTypeLabel } from './utils';

interface IProps {
  types: QuotePartNoteType[];
  isOpen?: boolean;
  level?: number;
  onClose?: (reset?: UseFormReset<IQuotePartRemoveNotesForm>) => void;
  onSubmit?: (
    reset?: UseFormReset<IQuotePartRemoveNotesForm>
  ) => (data: IQuotePartRemoveNotesForm) => void;
}

export const QuotePartRemoveNotesForm = ({
  types,
  isOpen = false,
  level,
  onClose,
  onSubmit,
}: IProps) => {
  const { t } = useTranslation();

  const { control, errors, setValue, trigger, watch } = useFormContext();

  const selectedTypes = watch('types') || {};
  const selectedTypesCount = types.filter((type) => !!selectedTypes[type]).length;

  const areAllTypesSelected = types.length > 0 && selectedTypesCount === types.length;

  const handleOnToggleAllTypes = (event: ChangeEvent<HTMLInputElement>) => {
    // shouldDirty keeps the selection from being wiped by the FormProvider reset effect
    // (keepDirtyValues) while the drawer lingers open on a defaultValues change.
    types.forEach((type) =>
      setValue(`types.${type}`, event.target.checked, { shouldValidate: true, shouldDirty: true })
    );

    // setValue's shouldValidate only unsets the leaf path it is given, and the "at least one type"
    // error is recorded on the parent `types` node — revalidate it explicitly to clear it.
    if (errors?.types) {
      trigger('types');
    }
  };

  return (
    <Form
      title={t('titleRemoveNotes')}
      level={level}
      isOpen={isOpen}
      onClose={onClose}
      submitBtnLabel={t('buttonLabelRemove')}
      onSubmit={onSubmit}
    >
      <Stack gap={1}>
        <FormControl size={Size.LG}>
          <FormLabel required>{t('labelSelectNoteTypesToRemove')}</FormLabel>
        </FormControl>

        <Card color={Color.NEUTRAL} variant={Variant.PLAIN}>
          <CardContent>
            <Stack gap={1}>
              <Checkbox
                label={t('labelAllTypes')}
                checked={areAllTypesSelected}
                indeterminate={selectedTypesCount > 0 && !areAllTypesSelected}
                onChange={handleOnToggleAllTypes}
                sx={{ width: '100%' }}
              />

              <Divider />

              {types.map((type) => (
                <FormField
                  key={type}
                  name={`types.${type}`}
                  type={InputType.CHECKBOX}
                  inputProps={{
                    control,
                    error: errors?.types?.[type],
                    label: getNoteTypeLabel(type),
                    sx: { width: '100%' },
                  }}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Form>
  );
};
